import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Pagination from "react-js-pagination";
import { Box } from "../../styled/Elements";
import ActionButtons from "./components/TableComponents";
import { useTranslation } from "react-i18next";
import { NoRecord } from "../../admin/NoRecord/NoRecord";
import { useUser } from "../../../context/userContext";
import { getModuleOperations } from "../../../utils";

const ListTable = ({
  columns,
  rows,
  typeId,
  totalCount,
  perPage,
  activePage,
  setActivePage,
  onEdit,
  onDelete,
  onStatusToggle,
  onOpen,
  options,
  onInfoView,
  onTransfer,
  onViewDetails,
  className,
  updateVisibilityStatus,
  onVisibility,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();
  const userId = Number(user?.id);

  const [pageHrefs, setPageHrefs] = useState([]);

  const filterOptions = (row, options) => {
    const filterOut = (opts, excluded) =>
      opts.filter((option) => !excluded.includes(option));
    const restrictedOptions = ["delete", "status", "edit", "transfer"];

    if (typeId === 4) {
      if (Number(user?.user_type_id) === 4)
        return filterOut(options, ["delete", "status", "edit"]);
      if (
        Number(user?.user_type_id) === 3 &&
        !user?.is_temporary_admin_director &&
        Number(user?.office_location?.id) !==
          Number(row?.user_detail?.location_id) &&
        Number(user?.department?.id) !== Number(row?.user_detail?.department_id)
      )
        return filterOut(options, ["delete", "status", "edit"]);
    }

    if ([1, 2, 3].includes(typeId)) {
      const {
        current_owner,
        current_owner_department_id,
        access_granted_requests,
        user_have_child_access,
      } = row || {};
      const { department } = user || {};
      const departmentId = Number(department?.id);
      const settingId = row?.setting_id ?? row?.common_settings_id;
      const entity_details =
        row?.cabinet_detail ??
        row?.cabinetfile_detail ??
        row?.file_archive_detail;

      const isLatestOwner = current_owner && userId === Number(current_owner);

      const isUserSelected = entity_details?.selected_users?.includes(userId);
      const isDepartmentSelected =
        entity_details?.selected_departments &&
        Array.isArray(entity_details?.selected_departments)
          ? entity_details?.selected_departments?.includes(departmentId)
          : entity_details?.selected_departments &&
            !Array.isArray(entity_details?.selected_departments) &&
            Number(entity_details?.selected_departments) === departmentId;

      const isOpenToUser =
        (settingId === 3 &&
          Number(current_owner_department_id) === departmentId) ||
        (settingId === 4 && isUserSelected) ||
        (settingId === 5 && isUserSelected) ||
        (settingId === 6 && isDepartmentSelected) ||
        (settingId === 7 && isDepartmentSelected);

      const hasAccessThroughRequest =
        access_granted_requests &&
        Array.isArray(access_granted_requests) &&
        access_granted_requests?.some(
          (item) => Number(item?.from_user_id) === userId
        );

      if (isLatestOwner) {
        return options;
      }
      if (isOpenToUser || hasAccessThroughRequest || user_have_child_access) {
        return filterOut(options, restrictedOptions);
      }

      // const hasAccessThroughRequest =
      //   row?.access_granted_requests &&
      //   Array.isArray(row?.access_granted_requests) &&
      //   row?.access_granted_requests?.some(
      //     (item) => Number(item?.from_user_id) === userId
      //   );

      if (user?.user_type_id == 4 && !getModuleOperations(5, 2)) {
        return filterOut(options, ["open", ...restrictedOptions]);
      }

      return filterOut(options, restrictedOptions);
    }

    return options;
  };

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
    const currentUrl = window.location.href.split("?")[0];
    const newUrl = `${currentUrl}?page=${pageNumber}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  useEffect(() => {
    const url = window.location.href.split("?")[0];
    const hrefs = Array.from(
      { length: Math.ceil(totalCount / perPage) },
      (_, index) => `${url}?page=${index + 1}`
    );
    setPageHrefs(hrefs);
  }, [totalCount, perPage]);

  return (
    <>
      <TableWrapper>
        <Box className="table-responsive custom-scroll overflow-x-auto">
          <table className={`w-100 ${className ? className : ""}`}>
            <thead>
              <tr>
                {columns.map((col, key) => (
                  <th
                    key={key}
                    className={`text-nowrap ${
                      col.name === "actions" && "actions-column"
                    }`}
                  >
                    {t(col.label)}
                  </th>
                ))}
              </tr>
            </thead>
            {rows && rows.length > 0 ? (
              <tbody>
                {rows?.map((row, index) => (
                  <tr key={index}>
                    {columns?.map((col, key) => (
                      <td
                        key={key}
                        className={`${
                          row[col.name] &&
                          row[col.name].length > 30 && 
                          "TableColumnWidth"
                        }`}

                        style={{
                          minWidth: col.name === "date_time" ? '200px' : '',
                        }}
                      >
                        {col.name === "actions" ? (
                          <ActionButtons
                            onVisibility={() => {
                              const visibility = parseInt(
                                row?.visibility_for_temp
                              );
                              let VisibilityForTemp;
                              if (visibility === 0) {
                                VisibilityForTemp = 1;
                              } else if (visibility === 1) {
                                VisibilityForTemp = 2;
                              }
                              onVisibility(row, VisibilityForTemp);
                            }}
                            updateVisibilityStatus={updateVisibilityStatus}
                            row={row}
                            options={filterOptions(row, options)}
                            typeId={typeId}
                            onEdit={() => onEdit(row)}
                            onDelete={() => onDelete(row)}
                            onOpen={() => onOpen(row)}
                            onStatusToggle={() => onStatusToggle(row)}
                            onInfoView={onInfoView}
                            status={row.status}
                            onTransfer={() => onTransfer(row)}
                            onViewDetails={onViewDetails}
                            tooltipData={row.infoData}
                            department={row.department}
                          />
                        ) : col.name === "id" ? (
                          (activePage - 1) * perPage + index + 1
                        ) : col.render ? (
                          col.render(row[col.name], row, t, language, user)
                        ) : (
                          row[col.name]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan="100%">
                    <NoRecord />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </Box>
        {rows && Array.isArray(rows) && rows.length > 0 && totalCount && (
          <PaginationContainer>
            <Pagination
              itemClass="page-item"
              linkClass="page-link"
              activePage={activePage}
              itemsCountPerPage={perPage}
              totalItemsCount={totalCount}
              pageRangeDisplayed={5}
              onChange={handlePageChange}
              // getPageUrl={(pageNumber) => pageHrefs[pageNumber - 1]}
            />
          </PaginationContainer>
        )}
      </TableWrapper>
    </>
  );
};

export const TableWrapper = styled.div`
  padding: 1rem;

  table {
    border-collapse: separate;
    border-spacing: 0 4px;
    tr {
      border-radius: 10px;
    }
    thead tr {
      background-color: none;
    }
    th {
      font-weight: 600;
      font-size: 14px;
      color: var(--text);
      padding: 12px 16px;
      background-color: inherit;
      border: none;
      text-align: center;
    }

    .actions-column {
      width: 100px;
    }

    tbody tr {
      td {
        max-width: 400px;
        background-color: var(--white);
        border-bottom: 1px solid var(--borderLight);
        padding: 8px 16px;
        color: var(--text);
        font-size: 14px;
        vertical-align: middle;
        text-align: center;
      }
      td:first-child {
        border-radius: 10px 0 0 10px;
        body.rtl & {
          border-radius: 0px 10px 10px 0px;
        }
      }
      td:last-child {
        border-radius: 0 10px 10px 0px;
        body.rtl & {
          border-radius: 10px 0 0 10px;
        }
      }
      .action-buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        button {
          background-color: var(--white);
          color: var(--text2);
          border: none;
          padding: 0.5rem 1rem;
          cursor: pointer;
          font-size: 14px;
          border-radius: 10px;
          border: 1px solid var(--borderLight);
          &:hover {
            color: var(--text);
            background-color: var(--borderLight);
          }
        }
      }
    }
    &.tableSecondary {
      border-collapse: collapse;
      border-spacing: 0 0px;
      tbody tr {
        border: 1px solid var(--borderLight);
        border-radius: 10px 0 0 10px;
        overflow: hidden;
        td {
          padding: 12px 16px;
          border: none;
        }
        td:first-child {
          border-radius: 10px 0 0 10px;
        }

        /* td:first-child {
                border-radius: 10px 0 0 10px;
            } */
      }
    }
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
`;

export default ListTable;
